import { LayoutGrid } from "lucide-react";
import React from "react";
import {
  Create,
  Datagrid,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  NumberInput,
  ImageInput,
  ImageField,
  required,
  useNotify,
  useRedirect,
  useRefresh,
  useRecordContext,
  DateField,
} from "react-admin";
import { CardListActions } from "../../components";

const PageDescription = () => (
  <div className="page-description">
    <h1>Ad Locations</h1>
    <p>
      Manage ad locations where ads can be displayed on the site (e.g.,
      homepage, sidebar, player page).
    </p>
  </div>
);

const AddEditPageDescription = () => (
  <div className="page-description">
    <h1>Add or Edit Ad Location</h1>
    <p>
      Configure an ad location with its display dimensions and strategies for
      ordering and displaying ads.
    </p>
  </div>
);

const orderingStrategyChoices = [
  { id: "random", name: "Random" },
  { id: "in_order", name: "In Order" },
];

const displayStrategyChoices = [
  { id: "all", name: "Show All" },
  { id: "up_to_n", name: "Show Up To N" },
];

const fillerPlacementStrategyChoices = [
  { id: "random", name: "Random" },
  { id: "after_non_filler", name: "After Non-Filler Ads" },
  { id: "only_when_no_non_fillers", name: "Only When No Non-Fillers" },
];

// Display current preview image in edit form
const CurrentPreviewImage = () => {
  const record = useRecordContext();
  if (!record?.preview_image_url) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <strong>Current Preview Image:</strong>
      <div style={{ marginTop: 8 }}>
        <img
          src={record.preview_image_url}
          alt="Location preview"
          style={{ maxWidth: 400, maxHeight: 250, border: "1px solid #ddd" }}
        />
      </div>
    </div>
  );
};

// Thumbnail for list view
const PreviewImageThumbnail = () => {
  const record = useRecordContext();
  if (!record?.preview_image_url)
    return <span style={{ color: "#999" }}>-</span>;

  return (
    <img
      src={record.preview_image_url}
      alt="Preview"
      style={{ maxWidth: 60, maxHeight: 40, objectFit: "contain" }}
    />
  );
};

const filters = [
  <TextInput source="id" label="ID" key="id" alwaysOn />,
  <TextInput source="slug" label="Slug" key="slug" alwaysOn />,
];

function ListView(props) {
  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Ad Locations"
      >
        <Datagrid rowClick="edit">
          <TextField source="id" label="ID" />
          <PreviewImageThumbnail label="Preview" />
          <TextField source="slug" label="Slug" />
          <TextField source="dimensions_default" label="Dimensions" />
          <TextField source="ordering_strategy" label="Ordering" />
          <TextField source="display_strategy" label="Display" />
          <DateField source="created_at" label="Created" showTime />
        </Datagrid>
      </List>
    </>
  );
}

const AdLocationForm = ({ redirect }) => {
  return (
    <SimpleForm redirect={redirect}>
      <AddEditPageDescription />

      <TextInput
        source="slug"
        label="Slug"
        validate={[required()]}
        helperText="Unique identifier for this location (e.g., 'homepage', 'sidebar', 'player_page')"
      />

      <h4>Dimensions</h4>
      <TextInput
        source="dimensions_default"
        label="Default Dimensions"
        validate={[required()]}
        helperText="Format: WIDTHxHEIGHT (e.g., '1200x400')"
      />
      <TextInput
        source="dimensions_mobile"
        label="Mobile Dimensions"
        helperText="Optional mobile dimensions (e.g., '375x200')"
      />

      <h4>Ordering & Display</h4>
      <SelectInput
        source="ordering_strategy"
        label="Ordering Strategy"
        choices={orderingStrategyChoices}
        validate={[required()]}
        defaultValue="random"
        helperText="How to order non-filler ads"
      />
      <SelectInput
        source="display_strategy"
        label="Display Strategy"
        choices={displayStrategyChoices}
        validate={[required()]}
        defaultValue="all"
        helperText="Whether to show all ads or limit to a specific count"
      />
      <NumberInput
        source="display_count"
        label="Display Count"
        helperText="Number of ads to display (only used when Display Strategy is 'Show Up To N')"
      />

      <h4>Filler Ads</h4>
      <SelectInput
        source="filler_ordering_strategy"
        label="Filler Ordering Strategy"
        choices={orderingStrategyChoices}
        validate={[required()]}
        defaultValue="random"
        helperText="How to order filler ads"
      />
      <SelectInput
        source="filler_placement_strategy"
        label="Filler Placement Strategy"
        choices={fillerPlacementStrategyChoices}
        validate={[required()]}
        defaultValue="after_non_filler"
        helperText="Where to place filler ads relative to non-filler ads. Use 'Only When No Non-Fillers' for fallback-only fillers."
      />

      <h4>Carousel</h4>
      <NumberInput
        source="shift_interval_ms"
        label="Shift Interval (ms)"
        helperText="Milliseconds between ad rotations in a carousel (leave empty to disable rotation)"
      />

      <h4>Preview Image</h4>
      <CurrentPreviewImage />
      <TextInput
        source="preview_image_url"
        label="Preview Image URL"
        fullWidth
        helperText="Manually enter a URL to a preview image (will be overridden if you upload a file)"
      />
      <ImageInput
        source="FILE_preview_image"
        label="Or Upload an Image"
        accept="image/*"
        placeholder={
          <p>
            Upload an image showing where this ad location appears (takes
            precedence over URL)
          </p>
        }
      >
        <ImageField source="src" title="title" />
      </ImageInput>
    </SimpleForm>
  );
};

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Ad location updated successfully!", { type: "success" });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdLocationForm redirect={false} />
    </Edit>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Ad location created successfully!", { type: "success" });
          refresh();
          redirect("list", "ad_locations");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdLocationForm redirect="list" />
    </Create>
  );
}

export const AdLocations = {
  category: "ads",
  name: "ad_locations",
  icon: LayoutGrid,
  list: ListView,
  edit: EditView,
  create: CreateView,
  options: { label: "Ad Locations" },
};
